/**
 * 侧边栏菜单：由路由表生成，分组、排序与旧版 utils/menu-generator.ts 一致；
 * 只保留当前管理员有权限的页面。
 */
import type { ComponentType } from 'react'
import {
  BarChartOutlined,
  DashboardOutlined,
  FileTextOutlined,
  RobotOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons'
import { canAccess, type AccessChecker, type RouteMeta } from './guards'

export interface MenuItem {
  key: string
  title: string
  icon?: ComponentType
  path?: string
  order?: number
  children?: MenuItem[]
}

// 生成菜单需要的路由信息（与 TanStack Router 解耦，便于测试）
export interface MenuSourceRoute {
  path: string
  meta?: RouteMeta
  children?: MenuSourceRoute[]
}

// 按 order 的百位数分组
const MENU_GROUPS = [
  { key: 'dashboard', title: '仪表板', icon: DashboardOutlined, order: 100, range: [100, 199] },
  { key: 'user', title: '用户管理', icon: UserOutlined, order: 200, range: [200, 299] },
  { key: 'content', title: '内容管理', icon: FileTextOutlined, order: 300, range: [300, 399] },
  { key: 'ai-system', title: 'AI系统', icon: RobotOutlined, order: 400, range: [400, 499] },
  { key: 'analytics', title: '数据分析', icon: BarChartOutlined, order: 500, range: [500, 599] },
  { key: 'system-settings', title: '系统设置', icon: SettingOutlined, order: 600, range: [600, 699] },
  { key: 'user-settings', title: '个人设置', icon: UserOutlined, order: 700, range: [700, 799] }
]

function toMenuItem(route: MenuSourceRoute, checker: AccessChecker): MenuItem | null {
  const meta = route.meta
  // 没有 order 的是详情页等非顶级菜单
  if (!meta?.order || meta.hideInMenu || !canAccess(meta, checker)) return null

  const children = (route.children ?? [])
    .filter((child) => !child.meta?.hideInMenu && canAccess(child.meta, checker))
    .map((child) => ({ key: child.path, title: child.meta?.title ?? child.path, icon: child.meta?.icon, path: child.path }))

  return {
    key: route.path,
    title: meta.title ?? route.path,
    icon: meta.icon,
    path: route.path,
    order: meta.order,
    ...(children.length > 0 ? { children } : {})
  }
}

export function buildMenu(routes: MenuSourceRoute[], checker: AccessChecker): MenuItem[] {
  const items = routes
    .map((route) => toMenuItem(route, checker))
    .filter((item): item is MenuItem => item !== null)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))

  const menu: MenuItem[] = []
  for (const group of MENU_GROUPS) {
    const groupItems = items.filter((item) => item.order! >= group.range[0] && item.order! <= group.range[1])
    if (groupItems.length === 0) continue
    // 组内只有一项时直接显示，不套一层分组
    if (groupItems.length === 1) {
      menu.push(groupItems[0])
      continue
    }
    menu.push({ key: group.key, title: group.title, icon: group.icon, order: group.order, children: groupItems })
  }
  return menu
}

// 拍平成可跳转的菜单项，供 Header 搜索使用
export function flattenMenu(menu: MenuItem[]): MenuItem[] {
  return menu.flatMap((item) => {
    if (item.children) return flattenMenu(item.children)
    return item.path ? [item] : []
  })
}

// 从顶层到当前页面菜单项的路径（详情页如 /themes/1 归到 /themes），找不到时为空
export function findMenuTrail(menu: MenuItem[], pathname: string): MenuItem[] {
  for (const item of menu) {
    if (item.children) {
      const trail = findMenuTrail(item.children, pathname)
      if (trail.length > 0) return [item, ...trail]
    } else if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) {
      return [item]
    }
  }
  return []
}
